import { memo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Boxes, Radio, Users } from 'lucide-react';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { PlayerProfile } from '../PlayerProfile';
import { LegacyMinecraftText } from '../LegacyMinecraftText';
import { formatMspt, formatTps } from '../../utils/starchart';
import type { StarchartLiveStatus } from '../../types/starchart';

interface LiveServerCardProps {
  live: StarchartLiveStatus | null;
}

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-2 py-1.5">
    <span className="text-xs text-glass-400">{label}</span>
    <span className="text-xs font-medium text-white tabular-nums truncate">{value}</span>
  </div>
);

/**
 * What the server is doing right now, from the status socket.
 *
 * This is the half of the picture the standalone StarChart site could never show: it only ever
 * had historical aggregates, so "who is on right now" and "what version is it running" lived on
 * a different tool entirely. The roster renders through `PlayerProfile`, so every name links
 * straight to that player's ARGUS profile.
 */
export const LiveServerCard = memo(({ live }: LiveServerCardProps) => {
  if (!live) {
    return (
      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Live status" icon={Radio} />
        <EmptyState
          bare
          compact
          icon={Radio}
          title="Not reporting"
          description="No live instance is publishing status for this hostname. Historical data below is unaffected."
        />
      </div>
    );
  }

  const lastSeenMs = live.lastSeen * 1000;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Live status"
        icon={Radio}
        actions={
          <span
            className={cn('badge badge-dot', live.isOffline ? 'badge-danger' : 'badge-success')}
          >
            {live.isOffline
              ? live.isExtendedDowntime
                ? 'Extended downtime'
                : 'Offline'
              : 'Online'}
          </span>
        }
      />

      {live.motd && (
        <div className="subcard p-3 mt-3 font-mono text-xs whitespace-pre-wrap break-words">
          <LegacyMinecraftText text={live.motd} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mt-3 divide-y md:divide-y-0 divide-glass-200/10">
        <div className="divide-y divide-glass-200/10">
          <Fact
            label="Players"
            value={`${live.playerCount} / ${live.maxPlayers}`}
          />
          <Fact label="TPS" value={formatTps(live.tps)} />
          <Fact label="MSPT" value={formatMspt(live.mspt)} />
          <Fact label="Entities" value={live.entities.toLocaleString()} />
          <Fact label="Loaded chunks" value={live.chunks.toLocaleString()} />
        </div>
        <div className="divide-y divide-glass-200/10">
          <Fact label="Minecraft" value={live.mcVersion} />
          <Fact
            label="Modloader"
            value={`${live.modloader} ${live.modloaderVersion}`}
          />
          <Fact label="Quasar" value={live.quasarVersion} />
          <Fact label="Redis ping" value={`${live.redisPing}ms`} />
          <Fact
            label="Last backup"
            value={
              live.lastBackupTaken
                ? formatDistanceToNow(new Date(live.lastBackupTaken * 1000), { addSuffix: true })
                : 'Never'
            }
          />
        </div>
      </div>

      <p className="text-xs text-glass-500 mt-3">
        Last frame {formatDistanceToNow(new Date(lastSeenMs), { addSuffix: true })} from{' '}
        <span className="font-mono">{live.hostname}</span>.
      </p>

      <div className="mt-4 pt-4 border-t border-glass-200/10">
        <SectionHeader
          title="Online now"
          icon={Users}
          count={live.players.length}
          as="h4"
        />
        {live.players.length === 0 ? (
          <p className="text-xs text-glass-500 mt-2">
            <Boxes className="w-3.5 h-3.5 inline mr-1 align-text-bottom" />
            Nobody is on this server right now.
          </p>
        ) : (
          <div className="flex flex-wrap gap-x-4 gap-y-2 mt-2">
            {live.players.map((player) => (
              <PlayerProfile key={player.UUID} uuid={player.UUID} name={player.name} inline showUuid={false} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

LiveServerCard.displayName = 'LiveServerCard';
